/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/restrict-template-expressions */
import Error from 'next/error'
import React, { useCallback } from 'react'
import { CardProps } from 'src/common/components/Card'
import ErrorPage from 'next/error'
import { ExerciseDetailContent } from 'src/common/components/Exercise/ExerciseDetailContent'
import { ExerciseDetailHero } from 'src/common/components/Exercise/ExerciseDetailHero'
import { Lane } from 'src/common/components/Lane'
import { AppLayout } from 'src/common/components/Layout/AppLayout'
import { RadixTabs } from 'src/common/components/Tabs/RadixTabs'
import { useUser } from 'src/common/hooks/useUser'
import { prependAssetHost, prependAssetHostChallenge } from 'src/common/utils'
import {
    getEventById,
    getRelatedExercises,
    useGetExerciseVideoQuery,
    useTrackProgressMutation
} from 'src/services/mainAPI'
import { JoinEventRes } from 'src/services/types/joinEvent'
import { wrapper } from 'src/store'
import { ExerciseMetaSection } from '../../common/components/Exercise/ExerciseMetaSection'

type ExerciseDetailProps = {
    item: JoinEventRes
    relatedExercises: any
    error: any
}

const ExerciseDetail = ({ item, relatedExercises, error }: ExerciseDetailProps) => {
    if (!item || error) {
        return <ErrorPage statusCode={404} />
    }

    const user = useUser()
    const { data, isLoading } = useGetExerciseVideoQuery(item.summary.challengeId)
    const [mutate] = useTrackProgressMutation()
    const trackProgress = useCallback(
        (seconds: number) => {
            if (user && data?.exerciseDetails?.challengeImageId) {
                void mutate({
                    challengeImageId: data.exerciseDetails.challengeImageId,
                    progressSeconds: seconds
                })
            }
        },
        [data?.exerciseDetails?.challengeImageId]
    )

    const tabs =
        item !== null
            ? [
                  {
                      label: 'About',
                      value: 'about',
                      content: (
                          <>
                              <ExerciseDetailContent
                                  distanceUnit={'MIN'}
                                  totalDistance={
                                      isLoading
                                          ? 0
                                          : Number(
                                                Number(
                                                    data?.exerciseDetails?.duration / 60
                                                ).toFixed(0)
                                            )
                                  }
                                  title={item.summary.name}
                                  htmlString={item.summary.fundraisingMessage?.replace(
                                      /(<|&lt;)br\s*\/*(>|&gt;)/g,
                                      ''
                                  )}
                              />
                              <ExerciseMetaSection
                                  keywordSections={
                                      data?.exerciseDetails?.keywordSections ?? []
                                  }
                              />
                              <section className="pl-2 md:pl-0">
                                  <Lane
                                      title="Related Exercises"
                                      data={
                                          relatedExercises.challengeDiscovery?.groups[0]?.challenges
                                              ?.filter((item) => item.isExercise)
                                              ?.slice(0, 14)
                                              .map((item): CardProps => {
                                                  return {
                                                      cardType: 'challenge',
                                                      //   badgeType: getBadgeType(item, user),
                                                      duration: '',
                                                      imageURL: item.logo
                                                          ? prependAssetHostChallenge(
                                                                item.logo
                                                            )
                                                          : prependAssetHost(
                                                                '/icons/no-challenge-bg.png'
                                                            ),
                                                      excerciseType: '',
                                                      title: item.name,
                                                      href: `/exercises/${encodeURIComponent(
                                                          item.challengeUrl
                                                      )}`
                                                  }
                                              }) ?? []
                                      }
                                  />
                              </section>
                          </>
                      )
                  }
              ]
            : []

    return (
        <AppLayout
            pageTitle={item?.summary?.name}
            metaDescription={item?.summary?.metaDescription}
            metaTitle={`${item?.summary?.name} | Virtual Exercises | Yes.Fit`}
            metaKeywords={item?.summary?.metaKeywords}
            metaImage={prependAssetHostChallenge(item.summary.imageUrl)}
        >
            {error ? (
                <Error statusCode={401} />
            ) : (
                <div>
                    {isLoading ? (
                        <div className="max-w-screen-fullHd mx-auto relative">
                            <div className="aspect-w-16 aspect-h-9 relative bg-black bg-opacity-60">
                                <div className="flex items-center justify-center absolute z-50 text-white">
                                    <svg
                                        className="animate-spin -ml-1 mr-3 h-10 w-10 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <ExerciseDetailHero
                            challengeUrl={item.summary.challengeUrl}
                            hasAccess={item.summary.hasAccess}
                            mainImage={prependAssetHostChallenge(item.summary.imageUrl)}
                            video={{
                                title: item.summary.name,
                                id: item.summary.challengeId,
                                videoUrl: data?.exerciseDetails?.videoUrl,
                                videoThumbUrl: data?.exerciseDetails?.thumbnailUrl
                            }}
                            trackProgress={trackProgress}
                        />
                    )}
                    <div className="mx-auto max-w-screen-fullHd">
                        <RadixTabs tabs={tabs} />
                    </div>
                </div>
            )}
        </AppLayout>
    )
}

export const getServerSideProps = wrapper.getServerSideProps(
    (store) => async (context) => {
        const { id } = context.params
        const challengeUrl = id as string
        const { data: item, error } = await store.dispatch(
            getEventById.initiate(challengeUrl)
        )

        const { data: relatedExercises } = await store.dispatch(
            getRelatedExercises.initiate(item?.summary?.challengeId ?? null)
        )

        return { props: { item: item ?? null, relatedExercises, error: error ?? null } }
    }
)

export default ExerciseDetail
